import React, { useState, useEffect } from "react";
import { Form, Input, DatePicker, InputNumber, Button, Table, Card } from "antd";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";

const { TextArea } = Input;

const Timesheet = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState(null);

  // ✅ LOCAL STORAGE
  const username = localStorage.getItem("username");
  const role = localStorage.getItem("role");

  console.log("USERNAME:", username);
  console.log("ROLE:", role);

  // ✅ LOAD DATA
  useEffect(() => {
    if (!username) return;

    let url = `https://rhrmapi.up2dateinc.com/timesheet?username=${username}`;

    if (role === "admin") {
      url = `https://rhrmapi.up2dateinc.com/timesheet?username=ALL`;
    }

    fetch(url)
      .then((res) => res.json())
      .then((resData) => setData(resData))
      .catch((err) => console.error(err));
  }, [username, role]);

  // ✅ ADD / UPDATE
  const onFinish = async (values) => {
    const selectedDate = values.date;

    const newEntry = {
      key: editingKey || Date.now().toString(),
      date: selectedDate.format("YYYY-MM-DD"),
      day: selectedDate.format("dddd"),
      project: values.project,
      notes: values.notes,
      hours: values.hours,
      location: values.location,
      username,
    };

    try {
      if (editingKey) {
        // ✏️ UPDATE (frontend only)
        setData((prev) =>
          prev.map((item) =>
            item.key === editingKey ? newEntry : item
          )
        );

        setEditingKey(null);
      } else {
        // ➕ CREATE
        const res = await fetch("https://rhrmapi.up2dateinc.com/timesheet", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newEntry),
        });

        const saved = await res.json();
        setData((prev) => [...prev, saved]);
      }

      form.resetFields();
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ EDIT
  const handleEdit = (record) => {
    setEditingKey(record.key);

    form.setFieldsValue({
      date: record.date ? dayjs(record.date) : null,
      project: record.project,
      notes: record.notes,
      hours: record.hours,
      location: record.location,
    });
  };

  // ✅ DELETE
  const handleDelete = async (key) => {
    try {
      await fetch(
        `https://rhrmapi.up2dateinc.com/timesheet/${key}?username=${username}`,
        { method: "DELETE" }
      );

      setData((prev) => prev.filter((item) => item.key !== key));
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ CHART DATA
  const chartData = Object.values(
    data.reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = { date: item.date, hours: 0 };
      }
      acc[item.date].hours += Number(item.hours || 0);
      return acc;
    }, {})
  );

  // ✅ TABLE COLUMNS
  const columns = [
  ...(role === "admin"
    ? [
        {
          title: "User",
          dataIndex: "user", // 👈 comes from backend
        },
      ]
    : []),
    { title: "Date", dataIndex: "date" },
    { title: "Day", dataIndex: "day" },
    { title: "Project", dataIndex: "project" },
    { title: "Hours", dataIndex: "hours" },
    { title: "Location", dataIndex: "location" },
    { title: "Notes", dataIndex: "notes" },

    ...(role !== "admin"
      ? [
          {
            title: "Action",
            render: (_, record) => (
              <>
                <Button type="link" onClick={() => handleEdit(record)}>
                  Edit
                </Button>

                <Button danger onClick={() => handleDelete(record.key)}>
                  Delete
                </Button>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 20 }}>

      {/* FORM */}
      {role !== "admin" && (
        <Card title="Add Timesheet Entry" style={{ marginBottom: 20 }}>
          <Form form={form} layout="vertical" onFinish={onFinish}>
            
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="project" label="Project" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item name="notes" label="Notes">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="hours" label="Hours" rules={[{ required: true }]}>
              <InputNumber min={0} max={24} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="location" label="Location">
              <Input />
            </Form.Item>

            <Button type="primary" htmlType="submit" block>
              {editingKey ? "Update Entry" : "Add Entry"}
            </Button>
          </Form>
        </Card>
      )}

      {/* TABLE */}
      <Card title="Timesheet Records" style={{ marginBottom: 20 }}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey="key"
          pagination={{ pageSize: 5 }}
        />
      </Card>

      {/* GRAPH */}
      <Card title="Hours per Day (Graph)">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="hours" fill="#1890ff" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

    </div>
  );
};

export default Timesheet;